import React from 'react';

import { Box, Typography } from '@mui/material';

import { type StatusRowTone, statusRowSx } from '../../reportCards/statusRowSx';
import { formatDealOptionName } from '../../shared/format';

import {
  DEAL_OPTION_ALLOCATION_COLUMN,
  DEAL_OPTION_NAME_COLUMN,
  DEAL_OPTION_OCCUPANCY_COLUMN,
  ROOM_CARD_GRID_TEMPLATE_COLUMNS,
} from './roomCardLayout';
import type { RoomDealOptionView } from './types';

interface Props {
  dealOption: RoomDealOptionView;
  roomApproved: boolean;
}

// Green once the room is approved; orange on an allocation that differs.
function getRowTone(roomApproved: boolean, differs: boolean): StatusRowTone | undefined {
  if (roomApproved) {
    return 'success';
  }

  return differs ? 'warning' : undefined;
}

export default function RoomDealOptionRow({ dealOption, roomApproved }: Props) {
  const tone = getRowTone(roomApproved, dealOption.differs);

  return (
    <Box
      sx={{
        alignItems: 'center',
        ...statusRowSx(tone),
        borderTop: 1,
        borderTopColor: 'divider',
        columnGap: 2,
        display: 'grid',
        gridTemplateColumns: ROOM_CARD_GRID_TEMPLATE_COLUMNS,
        // The 4px status border plus 12px padding lines columns up with the card header's 16px.
        pl: 1.5,
        pr: 2,
        py: 1.25,
      }}
    >
      <Typography
        variant="body2"
        fontWeight={600}
        sx={{ gridColumn: DEAL_OPTION_NAME_COLUMN, overflowWrap: 'break-word' }}
      >
        {formatDealOptionName(dealOption.dealOptionName)}
      </Typography>
      <Typography variant="body2" sx={{ gridColumn: DEAL_OPTION_ALLOCATION_COLUMN }}>
        {dealOption.allocationLabel}
      </Typography>
      <Typography variant="body2" sx={{ gridColumn: DEAL_OPTION_OCCUPANCY_COLUMN }}>
        {dealOption.occupancy ?? '—'}
      </Typography>
    </Box>
  );
}
